import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { patchAccount } from "../api/social";
import { useAuth } from "../hooks/useAuth";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";

const USERNAME_RE = /^[a-z0-9-]{3,24}$/;
const DISMISS_KEY = "atlas:username-banner-dismissed";

// ─── UsernameClaimBanner ──────────────────────────────────────────────────────
// Dashboard nudge shown until the user either claims a username or dismisses
// it. Dismissal persists in localStorage (not synced server-side — reappears
// on another device, which is fine, it's a one-time nudge not a setting).

export function UsernameClaimBanner() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = React.useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1",
  );
  const [value, setValue] = React.useState("");

  const normalized = value.trim().toLowerCase();
  const valid = USERNAME_RE.test(normalized);

  const claimMutation = useMutation({
    mutationFn: (username: string) => patchAccount({ username }),
    onSuccess: async () => {
      setValue("");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  if (!user || user.username != null || dismissed) return null;

  function dismiss() {
    window.localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  return (
    <AtlasPanel style={{ position: "relative" }}>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-muted)",
          padding: 4,
        }}
      >
        <X size={16} />
      </button>

      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Community</p>
      <h3 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 6px", paddingRight: 24 }}>
        Claim your username
      </h3>
      <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5, margin: "0 0 14px" }}>
        Pick a username to post, comment and give awards. It also becomes your public profile link.
      </p>
      <div style={{ display: "flex", gap: 10, maxWidth: 380 }}>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value.toLowerCase())}
          placeholder="yourname"
          maxLength={24}
          style={{
            flex: 1,
            height: "var(--control-md)",
            padding: "0 12px",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            color: "var(--text-primary)",
            fontSize: 14,
            fontFamily: "var(--font-mono)",
            outline: "none",
          }}
        />
        <Button
          disabled={!valid || claimMutation.isPending}
          onClick={() => claimMutation.mutate(normalized)}
        >
          {claimMutation.isPending ? "Claiming…" : "Claim"}
        </Button>
      </div>
      {value.length > 0 && !valid && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8, marginBottom: 0 }}>
          3–24 characters: lowercase letters, numbers and hyphens only.
        </p>
      )}
      {claimMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 8, marginBottom: 0 }}>
          {errorMessage(claimMutation.error, "Failed to claim username")}
        </p>
      )}
    </AtlasPanel>
  );
}
